/**
 * Planetary Industry — colonies del char, card-grid style.
 *
 * Layout (de arriba a abajo):
 *   1. Header + LastUpdated + Refresh
 *   2. Stats strip — 4 StatTile (active colonies, ISK/h total, stale >24h,
 *      upgrade R5)
 *   3. Tier filter (Pills) — opcionalmente filtra por R1..R5 / stale
 *   4. Grid de ColonyCards (responsive: 1 col mobile, 2 col md, 3 col xl)
 *      - Card de colony: System + planet type + R5 pill + ISK/h (si hay
 *        detail cargado) + pin count + last_update age + "Stale" warning
 *        pill si age > 24h
 *      - Click selecciona → border accent → drives detail section abajo
 *      - La card "top" (mayor ISK/h entre las cargadas) recibe badge
 *        "★ Top" + border accent gold
 *   5. Detail de la colony seleccionada (lazy fetch al primer click) —
 *      pins agrupados por role (extractor, factory, storage, launchpad,
 *      command). Extractors con ISK/h.
 *   6. Footnote
 *
 * Persistencia:
 *   - "planetary.selected" → planet_id de la colony seleccionada (o null)
 *   - "planetary.tier"     → 'all' | 'r5' | 'r4' | 'stale'
 *
 * "todo automatizado" value: ves qué colonies están activas, cuánto ISK/h
 * te genera cada extractor (a precio Jita actual), y cuándo hay que volver
 * al cliente a relanzar extractors — sin abrir el juego.
 *
 * Caveats:
 *   - Schematic → output mapping está scope-out v1 (requiere SDE PI schemas).
 *     Mostramos solo el schematic_id; el nombre puede resolverse a futuro
 *     via Fuzzwork.
 *   - ESI last_update es la última vez que el cliente interactuó con la
 *     colony, no cuándo está activa realmente. Usamos age_hours como proxy.
 *   - ISK/h usa Jita BUY (qué te pagan los buy orders, no el sell). Para PI
 *     commodities esto es la métrica relevante — vos vendés, no comprás.
 *   - Total ISK/h arriba suma solo las colonies que el user ya inspeccionó
 *     (lazy fetch). Es por diseño — abrir una colony = "esta me importa".
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { Pill, StatTile, EmptyState, type PillTone } from '../components/ui'

type ColonySummary = {
  planet_id: number
  solar_system_id: number
  solar_system_name: string | null
  planet_type: string
  planet_type_name: string
  upgrade_level: number
  num_pins: number | null
  last_update: string | null
  age_hours: number | null
}

type ResolvedPin = {
  pin_id: number
  type_id: number
  type_name: string | null
  latitude: number
  longitude: number
  role: 'extractor' | 'factory' | 'storage' | 'command' | 'launchpad' | 'unknown'
  extractors?: Array<{
    product_type_id: number
    product_name: string | null
    quantity_per_cycle: number
    cycle_time_hours: number
    expiry_time: string
    expires_in_hours: number
    expiring_soon: boolean
    jita_buy: number | null
    isk_per_cycle: number | null
    isk_per_hour: number | null
  }>
  contents?: Array<{ type_id: number; name: string | null; quantity: number }>
  schematic_id?: number
}

type ColonyDetail = ColonySummary & {
  pins: ResolvedPin[]
  counts: {
    total: number
    extractors: number
    factories: number
    storage: number
    expiring_soon: number
  }
}

type TierFilter = 'all' | 'r5' | 'r4' | 'stale'

const ROLE_PILL: Record<ResolvedPin['role'], { label: string; tone: PillTone }> = {
  extractor: { label: 'Extractor', tone: 'gold' },
  factory: { label: 'Factory', tone: 'info' },
  storage: { label: 'Storage', tone: 'warn' },
  command: { label: 'Command', tone: 'muted' },
  launchpad: { label: 'Launchpad', tone: 'muted' },
  unknown: { label: 'Other', tone: 'muted' },
}

function readLS<T extends string>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  return (v as T) ?? fallback
}

function writeLS(key: string, value: string | number | null) {
  try {
    window.localStorage.setItem(key, String(value ?? ''))
  } catch {
    // localStorage lleno / deshabilitado — OK, no persistimos
  }
}

export function Planetary() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const queryClient = useQueryClient()
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['planetary-colonies', forceRefetchKey],
    queryFn: () => api.planetaryColonies(force),
  })

  const colonies: ColonySummary[] = data?.colonies ?? []

  // Persisted selection + tier filter.
  const [tierFilter, setTierFilter] = useState<TierFilter>(() =>
    readLS<TierFilter>('planetary.tier', 'all'),
  )
  const [selected, setSelected] = useState<number | null>(() => {
    const stored = readLS<string>('planetary.selected', '')
    if (!stored) return null
    const parsed = parseInt(stored, 10)
    return Number.isFinite(parsed) ? parsed : null
  })

  useEffect(() => writeLS('planetary.tier', tierFilter), [tierFilter])
  useEffect(
    () => writeLS('planetary.selected', selected),
    [selected],
  )

  // Roll-up ISK/h desde los details ya cargados en cache. Lazy load
  // (solo fetcheamos detail cuando el user abre una colony), así que este
  // total refleja solo lo que el user inspeccionó. Es por diseño — abrir
  // una colony = "esta me importa, mostrame su valor".
  const loadedDetails = queryClient.getQueriesData<ColonyDetail>({
    queryKey: ['planetary-colony'],
  })
  const rollup = useMemo(() => {
    let totalIskPerHour = 0
    let valuedExtractorCount = 0
    const perColony = new Map<number, number>()
    for (const [, d] of loadedDetails) {
      if (!d) continue
      let colonySum = 0
      for (const pin of d.pins) {
        for (const ext of pin.extractors ?? []) {
          if (ext.isk_per_hour !== null && ext.isk_per_hour !== undefined) {
            totalIskPerHour += ext.isk_per_hour
            valuedExtractorCount++
            colonySum += ext.isk_per_hour
          }
        }
      }
      perColony.set(d.planet_id, colonySum)
    }
    return { totalIskPerHour, valuedExtractorCount, perColony }
  }, [loadedDetails])

  // Tier-filter colonies.
  const filtered = useMemo(() => {
    if (tierFilter === 'all') return colonies
    if (tierFilter === 'r5') return colonies.filter((c) => c.upgrade_level === 5)
    if (tierFilter === 'r4') return colonies.filter((c) => c.upgrade_level === 4)
    if (tierFilter === 'stale')
      return colonies.filter(
        (c) => c.age_hours !== null && c.age_hours > 24,
      )
    return colonies
  }, [colonies, tierFilter])

  const activeColonies = colonies.length
  const staleCount = colonies.filter(
    (c) => c.age_hours !== null && c.age_hours > 24,
  ).length
  const r5Count = colonies.filter((c) => c.upgrade_level === 5).length

  // Para el bar relative en cada card.
  const maxColonyIsk = Math.max(1, ...Array.from(rollup.perColony.values()))
  // Hero: colony con mayor ISK/h de las loaded. Si ninguna está loaded,
  // null → no mostramos hero.
  const ranked = colonies
    .map((c) => ({ c, isk: rollup.perColony.get(c.planet_id) ?? 0 }))
    .filter((x) => x.isk > 0)
    .sort((a, b) => b.isk - a.isk)
  const hero = ranked[0]
  const heroColonyId = hero?.c.planet_id ?? null

  // Selected colony for detail section. Default to first colony if none.
  const selectedColony =
    colonies.find((c) => c.planet_id === selected) ?? colonies[0] ?? null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Planetary Industry</h1>
          <p className="text-eve-muted text-sm">
            Tus colonies PI, extractors próx. a expirar, factories y storage.
            ISK/h calculado a Jita buy (5min cache).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          variant="muted"
          size="sm"
          label="Active colonies"
          value={String(activeColonies)}
          sub={
            r5Count > 0
              ? `${r5Count} R5 · ${activeColonies - r5Count} R${activeColonies - r5Count > 0 ? '1-R4' : ''}`
              : 'ninguna R5'
          }
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={rollup.valuedExtractorCount > 0 ? 'accent' : 'muted'}
          label="ISK/h total"
          value={
            rollup.valuedExtractorCount > 0
              ? `${formatISKShort(rollup.totalIskPerHour)} ISK/h`
              : '—'
          }
          sub={
            rollup.valuedExtractorCount > 0
              ? `${rollup.valuedExtractorCount} extractor${rollup.valuedExtractorCount !== 1 ? 's' : ''} cargado${rollup.valuedExtractorCount !== 1 ? 's' : ''}`
              : 'abrí una colony para ver valor'
          }
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={staleCount > 0 ? 'negative' : 'muted'}
          label="Stale >24h"
          value={String(staleCount)}
          sub={staleCount > 0 ? '⚠ revisar en cliente' : 'todo fresco'}
        />
        <StatTile
          variant="muted"
          size="sm"
          label="Upgrade R5"
          value={String(r5Count)}
          sub={r5Count > 0 ? 'maxed CC' : 'sub-óptimo'}
        />
      </div>

      {/* Tier filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-eve-muted mr-1">
          Filter:
        </span>
        <FilterPill
          label="Todos"
          value={colonies.length}
          active={tierFilter === 'all'}
          tone="muted"
          onClick={() => setTierFilter('all')}
        />
        <FilterPill
          label="R5"
          value={r5Count}
          active={tierFilter === 'r5'}
          tone="gold"
          onClick={() => setTierFilter('r5')}
        />
        <FilterPill
          label="R4"
          value={colonies.filter((c) => c.upgrade_level === 4).length}
          active={tierFilter === 'r4'}
          tone="info"
          onClick={() => setTierFilter('r4')}
        />
        <FilterPill
          label="Stale >24h"
          value={staleCount}
          active={tierFilter === 'stale'}
          tone="warn"
          onClick={() => setTierFilter('stale')}
        />
      </div>

      {/* Hero — top valued colony (si hay alguna loaded) */}
      {hero && (
        <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-accent/30 rounded-md p-5 relative overflow-hidden">
          <div className="text-xs uppercase tracking-wider text-eve-muted">
            Top valued colony
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-3xl sm:text-4xl font-semibold text-eve-text">
              {hero.c.solar_system_name ?? `System #${hero.c.solar_system_id}`}
            </span>
            <Pill tone="gold" size="md">
              {hero.c.planet_type_name} · R{hero.c.upgrade_level}
            </Pill>
          </div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-2xl sm:text-3xl text-eve-accent font-bold">
              {formatISKShort(hero.isk)}
            </span>
            <span className="text-sm text-eve-muted">ISK/h</span>
            <span className="text-xs text-eve-muted-2">
              Jita buy · 5min cache
            </span>
          </div>
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <ColoniesGridSkeleton />
      ) : colonies.length === 0 ? (
        <EmptyState
          title="Sin colonies PI activas"
          description="Las colonies aparecen cuando instalás un Command Center en un planeta. Asegurate de que el scope esi-planets.manage_planets.v1 esté autorizado."
          variant="card"
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="Sin colonies con este filtro"
          description="Probá cambiando el tier o quitá el filtro."
          variant="card"
        />
      ) : (
        <>
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-lg font-semibold text-eve-text">
              Colonias
            </h2>
            <span className="text-[10px] text-eve-muted-2 italic">
              click una card para ver pins
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((colony) => {
              const isk = rollup.perColony.get(colony.planet_id) ?? 0
              const isHero = colony.planet_id === heroColonyId
              const isSelected =
                selectedColony?.planet_id === colony.planet_id
              const stale =
                colony.age_hours !== null && colony.age_hours > 24
              const barWidth =
                isk > 0 ? Math.max(2, (isk / maxColonyIsk) * 100) : 0
              return (
                <button
                  key={colony.planet_id}
                  type="button"
                  onClick={() => setSelected(colony.planet_id)}
                  className={`text-left rounded-md p-4 border transition-all relative outline-none focus-visible:ring-2 focus-visible:ring-eve-accent/50 ${
                    isSelected
                      ? 'border-eve-accent bg-eve-panel'
                      : isHero
                        ? 'border-eve-accent/40 bg-eve-panel hover:border-eve-accent/60 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30'
                        : 'border-eve-border bg-eve-panel hover:border-eve-accent/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/30'
                  }`}
                >
                  {isHero && (
                    <span className="absolute top-2 right-2 bg-eve-accent text-eve-bg text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                      ★ Top
                    </span>
                  )}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-lg text-eve-text truncate">
                        {colony.solar_system_name ??
                          `System #${colony.solar_system_id}`}
                      </div>
                      <div className="text-[10px] text-eve-muted font-mono">
                        {colony.planet_type_name} · #{colony.planet_id}
                      </div>
                    </div>
                    <Pill
                      tone={colony.upgrade_level === 5 ? 'gold' : 'muted'}
                      size="sm"
                    >
                      R{colony.upgrade_level}
                    </Pill>
                  </div>

                  {/* ISK/h (si está valued) */}
                  {isk > 0 ? (
                    <div className="font-mono font-bold tabular-nums tracking-tight text-2xl text-eve-accent">
                      {formatISKShort(isk)}
                      <span className="text-xs text-eve-muted ml-1 font-sans">
                        ISK/h
                      </span>
                    </div>
                  ) : (
                    <div className="font-mono text-sm text-eve-muted-2 italic">
                      sin valorar · click para ver pins
                    </div>
                  )}

                  {/* Magnitude bar */}
                  {isk > 0 && (
                    <div className="mt-2 h-1 bg-eve-bg rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-eve-accent/70"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  )}

                  {/* Footer chips */}
                  <div className="mt-3 flex gap-1.5 flex-wrap items-center">
                    <Pill tone="muted" size="xs">
                      {colony.num_pins ?? '—'} pins
                    </Pill>
                    <Pill tone="muted" size="xs">
                      {colony.last_update
                        ? formatAge(colony.age_hours)
                        : 'never updated'}
                    </Pill>
                    {stale && (
                      <Pill tone="warn" size="xs">
                        ⚠ stale
                      </Pill>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}

      {/* Detail de la colony seleccionada */}
      {selectedColony && (
        <ColonyDetailSection
          colony={selectedColony}
          onClose={() => setSelected(null)}
        />
      )}

      <p className="text-xs text-eve-muted italic">
        El ISK/h arriba suma solo las colonies que abriste (lazy fetch).
        Tocá una card para cargar su detail y ver pins por role, extractors
        con ISK/h a Jita buy, factories, storage, etc. Sin abrir el juego.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterPill({
  label,
  value,
  active,
  tone,
  onClick,
}: {
  label: string
  value: number
  active: boolean
  tone: PillTone
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="outline-none focus-visible:ring-1 focus-visible:ring-eve-accent/40 rounded-full"
    >
      <Pill tone={active ? tone : 'muted'} size="md">
        {label} · {value}
      </Pill>
    </button>
  )
}

/**
 * Detail section — lazy-fetch del colony detail al primer click. Una vez
 * cacheado en TanStack Query, los clicks siguientes son instantáneos.
 */
function ColonyDetailSection({
  colony,
  onClose,
}: {
  colony: ColonySummary
  onClose: () => void
}) {
  const detailQuery = useQuery({
    queryKey: ['planetary-colony', colony.planet_id],
    queryFn: () => api.planetaryColony(colony.planet_id),
    staleTime: 30 * 60 * 1000,
  })

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold text-eve-text">
            {colony.solar_system_name ??
              `System #${colony.solar_system_id}`}{' '}
            · detail
          </h2>
          <div className="text-[11px] text-eve-muted">
            {colony.planet_type_name} · R{colony.upgrade_level} ·{' '}
            {colony.num_pins ?? '—'} pins · last update{' '}
            {colony.last_update ? formatAge(colony.age_hours) : 'never'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {detailQuery.data && (
            <Pill tone="gold" size="sm">
              {detailQuery.data.counts.extractors} extractors
            </Pill>
          )}
          {detailQuery.data &&
            detailQuery.data.counts.expiring_soon > 0 && (
              <Pill tone="danger" size="sm">
                {detailQuery.data.counts.expiring_soon} expiring soon
              </Pill>
            )}
          <button
            type="button"
            onClick={onClose}
            className="text-[10px] uppercase tracking-wider text-eve-muted hover:text-eve-text border border-eve-border rounded px-2 py-0.5"
          >
            ✕ close
          </button>
        </div>
      </div>

      {detailQuery.isLoading && <PinsDetailSkeleton />}
      {detailQuery.isError && (
        <div className="text-eve-danger text-sm border border-eve-danger/30 rounded-md p-3 bg-eve-danger/5">
          Error cargando el detalle: {(detailQuery.error as Error).message}
        </div>
      )}
      {detailQuery.data && <ColonyDetail colony={detailQuery.data} />}
    </section>
  )
}

function ColonyDetail({ colony }: { colony: ColonyDetail }) {
  // Agrupar pins por role para mostrar en secciones claras.
  const grouped = new Map<ResolvedPin['role'], ResolvedPin[]>()
  for (const pin of colony.pins) {
    if (!grouped.has(pin.role)) grouped.set(pin.role, [])
    grouped.get(pin.role)!.push(pin)
  }

  const order: ResolvedPin['role'][] = [
    'extractor',
    'factory',
    'storage',
    'launchpad',
    'command',
    'unknown',
  ]

  return (
    <div className="space-y-4">
      {/* 4 mini-stats resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          variant="muted"
          size="sm"
          label="Pins total"
          value={String(colony.counts.total)}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone="accent"
          label="Extractors"
          value={String(colony.counts.extractors)}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone="accent"
          label="Factories"
          value={String(colony.counts.factories)}
        />
        <StatTile
          variant="muted"
          size="sm"
          tone={colony.counts.expiring_soon > 0 ? 'negative' : 'muted'}
          label="Expiring <6h"
          value={String(colony.counts.expiring_soon)}
          sub={colony.counts.expiring_soon > 0 ? '⚠ relanzar' : 'todo OK'}
        />
      </div>

      {/* Pin groups */}
      <div className="space-y-4">
        {order
          .filter((role) => grouped.has(role))
          .map((role) => (
            <PinGroup key={role} role={role} pins={grouped.get(role)!} />
          ))}
      </div>
    </div>
  )
}

function PinGroup({
  role,
  pins,
}: {
  role: ResolvedPin['role']
  pins: ResolvedPin[]
}) {
  const meta = ROLE_PILL[role]
  return (
    <div className="bg-eve-panel border border-eve-border rounded-md p-3">
      <div className="flex items-center gap-2 mb-2">
        <Pill tone={meta.tone} size="sm">
          {meta.label}
        </Pill>
        <span className="text-xs text-eve-muted">
          {pins.length} pin{pins.length !== 1 ? 's' : ''}
        </span>
      </div>
      <ul className="space-y-1.5">
        {pins.map((pin) => (
          <PinRow key={pin.pin_id} pin={pin} />
        ))}
      </ul>
    </div>
  )
}

function PinRow({ pin }: { pin: ResolvedPin }) {
  return (
    <li className="text-sm bg-eve-bg/30 border border-eve-border/50 rounded px-3 py-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="font-medium text-eve-text">
          {pin.type_name ?? `Pin #${pin.type_id}`}
        </span>
        <span className="text-[10px] text-eve-muted font-mono">
          #{pin.pin_id}
        </span>
      </div>

      {pin.extractors && pin.extractors.length > 0 && (
        <ul className="mt-2 space-y-1">
          {pin.extractors.map((ext, i) => (
            <li
              key={i}
              className={`text-xs flex items-baseline gap-2 flex-wrap ${
                ext.expiring_soon ? 'text-eve-warn' : 'text-eve-muted'
              }`}
            >
              <span className="font-mono">
                {ext.quantity_per_cycle}×{' '}
                {ext.product_name ?? `Type #${ext.product_type_id}`}
              </span>
              <span>·</span>
              <span>cycle {ext.cycle_time_hours}h</span>
              <span>·</span>
              <span className={ext.expiring_soon ? 'font-semibold' : ''}>
                {ext.expires_in_hours >= 0
                  ? `expires in ${ext.expires_in_hours}h`
                  : `expired ${Math.abs(ext.expires_in_hours)}h ago`}
              </span>
              <span>·</span>
              {ext.isk_per_hour !== null && ext.isk_per_hour !== undefined ? (
                <span
                  className="font-mono text-eve-accent font-semibold"
                  title={`${ext.quantity_per_cycle} × ${formatISKShort(ext.jita_buy ?? 0)} Jita buy / ${ext.cycle_time_hours}h`}
                >
                  {formatISKShort(ext.isk_per_hour)} ISK/h
                </span>
              ) : (
                <span className="font-mono text-eve-muted-2 italic">
                  sin precio Jita
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {pin.contents && pin.contents.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {pin.contents.map((c, i) => (
            <li key={i} className="text-xs text-eve-muted font-mono">
              {c.quantity.toLocaleString()}× {c.name ?? `Type #${c.type_id}`}
            </li>
          ))}
        </ul>
      )}

      {pin.schematic_id !== undefined && (
        <div className="mt-1 text-xs text-eve-muted font-mono">
          Schematic #{pin.schematic_id}
          <span className="text-[10px] ml-2 italic">(product lookup: future)</span>
        </div>
      )}
    </li>
  )
}

function formatAge(ageHours: number | null): string {
  if (ageHours === null) return 'never'
  if (ageHours < 1) return `${Math.round(ageHours * 60)}m ago`
  if (ageHours < 24) return `${ageHours}h ago`
  const days = Math.floor(ageHours / 24)
  return `${days}d ago`
}

/**
 * Skeleton del grid de colonies. Mimic la estructura visual de ColonyCard
 * (system + R5 pill + ISK/h + pin count) para que loaded → real data no
 * haga jump.
 */
function ColoniesGridSkeleton() {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
      aria-busy="true"
      aria-live="polite"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="border border-eve-border rounded-md p-4 bg-eve-panel space-y-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="h-5 w-1/2 bg-eve-border/40 rounded animate-pulse" />
            <div className="h-5 w-10 bg-eve-border/40 rounded-full animate-pulse" />
          </div>
          <div className="h-7 w-2/3 bg-eve-border/30 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-4 w-14 bg-eve-border/30 rounded-full animate-pulse" />
            <div className="h-4 w-16 bg-eve-border/30 rounded-full animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Pins detail skeleton — cuando el user hace click en una card, fetch del
 * detail endpoint (lazy). Skeleton mimic los pin groups (extractors,
 * factories, storage) que se renderizan cuando llega la data.
 */
function PinsDetailSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-4 w-16 bg-eve-border/40 rounded animate-pulse" />
          <div className="h-4 flex-1 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-4 w-20 bg-eve-border/40 rounded animate-pulse" />
        </div>
      ))}
    </div>
  )
}
